/**
 * Session storage and local storage utilities for persisting data
 */

/**
 * Get a value from sessionStorage
 * @param {string} key - The storage key
 * @param {*} fallback - Fallback value if key doesn't exist
 * @returns {*} The stored value or fallback
 */
export const getSessionItem = (key, fallback = null) => {
    const value = sessionStorage.getItem(key)
    return value !== null ? value : fallback
}

/**
 * Get a boolean value from sessionStorage
 * @param {string} key - The storage key
 * @param {boolean} fallback - Fallback value if key doesn't exist
 * @returns {boolean} The stored boolean value or fallback
 */
export const getSessionBoolean = (key, fallback = false) => {
    const value = sessionStorage.getItem(key)
    return value === 'true'
}

/**
 * Get a JSON object from sessionStorage
 * @param {string} key - The storage key
 * @param {*} fallback - Fallback value if key doesn't exist or parse fails
 * @returns {*} The parsed object or fallback
 */
export const getSessionJSON = (key, fallback = null) => {
    try {
        const value = sessionStorage.getItem(key)
        return value ? JSON.parse(value) : fallback
    } catch (error) {
        console.warn('Failed to parse sessionStorage key "${key}":', error)
        return fallback
    }
}

/**
 * Set a value in sessionStorage
 * @param {string} key - The storage key
 * @param {string} value - The value to store
 */
export const setSessionItem = (key, value) => {
    sessionStorage.setItem(key, value)
}

/**
 * Set a boolean value in sessionStorage
 * @param {string} key - The storage key
 * @param {boolean} value - The boolean value to store
 */
export const setSessionBoolean = (key, value) => {
    if (value) {
        sessionStorage.setItem(key, 'true')
    } else {
        sessionStorage.removeItem(key)
    }
}

/**
 * Set a JSON object in sessionStorage
 * @param {string} key - The storage key
 * @param {*} value - The object to store (will be JSON stringified)
 */
export const setSessionJSON = (key, value) => {
    if (value && (typeof value === 'object' ? Object.keys(value).length > 0 : true)) {
        sessionStorage.setItem(key, JSON.stringify(value))
    } else {
        sessionStorage.removeItem(key)
    }
}

/**
 * Remove a value from sessionStorage
 * @param {string} key - The storage key
 */
export const removeSessionItem = (key) => {
    sessionStorage.removeItem(key)
}

/**
 * Get a value from localStorage
 * @param {string} key - The storage key
 * @param {*} fallback - Fallback value if key doesn't exist
 * @returns {*} The stored value or fallback
 */
export const getLocalItem = (key, fallback = null) => {
    const value = localStorage.getItem(key)
    return value !== null ? value : fallback
}

/**
 * Set a value in localStorage
 * @param {string} key - The storage key
 * @param {string} value - The value to store
 */
export const setLocalItem = (key, value) => {
    localStorage.setItem(key, value)
}

/**
 * Get a boolean value from localStorage
 * @param {string} key - The storage key
 * @param {boolean} fallback - Fallback value if key doesn't exist
 * @returns {boolean} The stored boolean value or fallback
 */
export const getLocalBoolean = (key, fallback = false) => {
    const value = localStorage.getItem(key)
    return value === 'true'
}

/**
 * Set a boolean value in localStorage
 * @param {string} key - The storage key
 * @param {boolean} value - The boolean value to store
 */
export const setLocalBoolean = (key, value) => {
    if (value) {
        localStorage.setItem(key, 'true')
    } else {
        localStorage.removeItem(key)
    }
}

/**
 * Get a JSON object from localStorage
 * @param {string} key - The storage key
 * @param {*} fallback - Fallback value if key doesn't exist or parse fails
 * @returns {*} The parsed object or fallback
 */
export const getLocalJSON = (key, fallback = null) => {
    try {
        const value = localStorage.getItem(key)
        return value ? JSON.parse(value) : fallback
    } catch (error) {
        console.warn('Failed to parse localStorage key "${key}":', error)
        return fallback
    }
}

/**
 * Set a JSON object in localStorage
 * @param {string} key - The storage key
 * @param {*} value - The object to store (will be JSON stringified)
 */
export const setLocalJSON = (key, value) => {
    if (value && (typeof value === 'object' ? Object.keys(value).length > 0 : true)) {
        localStorage.setItem(key, JSON.stringify(value))
    } else {
        localStorage.removeItem(key)
    }
}

/**
 * Remove a value from localStorage
 * @param {string} key - The storage key
 */
export const removeLocalItem = (key) => {
    localStorage.removeItem(key)
}